import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Cog } from "lucide-react";
import { COMPANY, IMAGES } from "../data/site";

const MaskedLine = ({ children, delay }) => (
  <div className="overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
        <img
          src={IMAGES.coalHero}
          alt="Macro texture of black coal"
          className="h-full w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-black/55" />
      </motion.div>

      <div className="mx-auto w-full max-w-[1400px] px-6 pb-16 pt-40 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-mono-label mb-8 text-xs text-copper"
          data-testid="hero-eyebrow"
        >
          {COMPANY.name} — {COMPANY.location}
        </motion.p>

        <h1 className="font-display text-5xl font-semibold uppercase leading-[0.95] tracking-tight text-zinc-100 sm:text-7xl lg:text-8xl" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Copper ingots.</MaskedLine>
          <MaskedLine delay={0.5}>
            10% below <span className="text-copper">LME.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base text-zinc-400 md:text-lg"
          data-testid="hero-subtext"
        >
          Zambian Copperbelt ingots at 10% off LME prices — minimum 100 MT, shipping
          worldwide. 30% advance, balance on arrival at your destination port.
          Coal still in stock and ready to ship.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-quote-btn"
            className="group flex items-center gap-2 bg-copper px-7 py-4 font-mono-label text-xs text-ink transition-colors duration-300 hover:bg-copper-light"
          >
            Request Copper Quote
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#products"
            data-testid="hero-products-btn"
            className="border border-zinc-700 px-7 py-4 font-mono-label text-xs text-zinc-200 transition-colors duration-300 hover:border-copper hover:text-copper-light"
          >
            View Products
          </a>
          <Cog size={30} className="animate-spin-slow ml-2 text-zinc-700" aria-hidden="true" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 grid grid-cols-2 gap-6 border-t border-zinc-800 pt-8 md:grid-cols-4"
          data-testid="hero-stats"
        >
          {[
            ["Copper Ingots", "10% below LME"],
            ["Min. Order", "100 MT"],
            ["Payment", "30% advance"],
            ["Coal", "In stock now"],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="font-mono-label text-[10px] text-zinc-500">{k}</p>
              <p className="mt-2 font-display text-lg font-medium uppercase text-zinc-200">{v}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, rotate: -12 }}
        animate={{ opacity: 1, rotate: -6 }}
        transition={{ duration: 0.8, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className="pointer-events-none absolute right-10 top-28 hidden lg:block"
      >
        <div className="border-4 border-double border-copper/60 px-5 py-3 font-stencil text-sm text-copper/80" data-testid="hero-stamp">
          10% off LME prices
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 hidden items-center gap-2 lg:flex"
      >
        <span className="font-mono-label text-[10px] text-zinc-500">Scroll</span>
        <ArrowDown size={14} className="animate-bounce text-copper" />
      </motion.div>
    </section>
  );
};
